import { adapterPlanogramItemToPlanogramItemCommon } from "@CommonAdapters/pog";
import type { PlanogramBayNonCircular } from "@CommonTypes/merchflow/pog/bay";
import { Pog } from "@CommonTypes/merchflow/pog/pog";
import type { PlanogramItem } from "@CommonTypes/merchflow/pog/item";
import type { BaseProduct } from "@CommonTypes/merchflow/pog/product";
import { chunk } from "es-toolkit";
import type { A4Page } from "./types";

const isItemInBayRange = (item: PlanogramItem, bayXLeft: number, bayXRight: number) => {
  const { xLeft, xRight } = item.getCoordinates();
  const xCenter = (xLeft + xRight) / 2;
  return xCenter >= bayXLeft && xCenter < bayXRight;
};

export const selectSingleBay = (
  bays: PlanogramBayNonCircular[],
  bayId: string,
  section: number,
) => {
  const selectedBayById = bays.find((bay) => bay.uniqueId === bayId);
  if (selectedBayById) {
    return selectedBayById;
  }

  return bays[section - 1] ?? null;
};

export const createSingleBayPog = ({
  pog,
  bayId,
  section,
}: {
  pog: Pog;
  bayId: string;
  section: number;
}) => {
  const pogNonCircular = pog.toNonCircular();
  const selectedBay = selectSingleBay(pogNonCircular.planogram.bays, bayId, section);
  const sourceBay = selectedBay ? pog.planogram.getBay(selectedBay.uniqueId) : null;
  const bayXLeft = sourceBay?.getXLeft() ?? 0;
  const bayXRight = sourceBay?.getXRight() ?? 0;
  const sanitizedBay = selectedBay
    ? {
        ...selectedBay,
        shelves: selectedBay.shelves.map((shelf) => ({
          ...shelf,
          items:
            pog.planogram
              .getShelf(shelf.uniqueId)
              ?.getMergedShelves()
              .flatMap((mergedShelf) => mergedShelf.items)
              .filter((item) => isItemInBayRange(item, bayXLeft, bayXRight))
              .sort((item1, item2) => item1.getCoordinates().xLeft - item2.getCoordinates().xLeft)
              .map((item) => ({
                ...adapterPlanogramItemToPlanogramItemCommon(item),
                shelf: shelf.uniqueId,
              })) || [],
          mergedLeft: null,
          mergedRight: null,
        })),
      }
    : null;

  pogNonCircular.planogram.bays = sanitizedBay ? [sanitizedBay] : [];

  return new Pog(pogNonCircular);
};

export const buildA4PagesForSingleBay = ({
  pog,
  bayId,
  section,
  // itemsPerPage = 26 is the maximum number of items (and shelf section headers)
  // that can comfortably fit within a single A4 page without overflowing the container heights.
  itemsPerPage = 26,
  t,
}: {
  pog: Pog;
  bayId: string;
  section: number;
  itemsPerPage?: number;
  t?: (key: string, options?: Record<string, string | number>) => string;
}): A4Page[] => {
  const oneBayPog = createSingleBayPog({ pog, bayId, section });
  const bay = oneBayPog.planogram.bays[0];

  if (!bay) {
    return [];
  }

  const bayItems: A4Page = [];
  let itemNo = 0;

  for (const shelf of bay.shelves) {
    const notchSuffix = t
      ? t("shelfNotchPrefix", { notch: shelf.getNotchNo() })
      : ` N:${shelf.getNotchNo()}`;
    const shelfName = `${section}.${shelf.getNo()}${notchSuffix}`;
    bayItems.push({
      shelfName,
      type: "SHELF",
      shelf,
    });

    shelf.items.forEach((item) => {
      bayItems.push({
        item,
        shelfName,
        type: "ITEM",
        no: ++itemNo,
      });
    });
  }

  return chunk(bayItems, itemsPerPage).map((page) => {
    const tempPage = [...page];
    const firstItem = tempPage[0];

    if (!firstItem) {
      return tempPage;
    }

    // Add first shelf if page starts with an item row.
    if (firstItem.type !== "SHELF") {
      tempPage.unshift({
        shelfName: firstItem.shelfName,
        type: "SHELF",
        shelf: firstItem.item.shelf,
      });
    }

    // Remove trailing orphan shelf header.
    const lastItem = tempPage[tempPage.length - 1];
    if (lastItem?.type === "SHELF") {
      tempPage.pop();
    }

    return tempPage;
  });
};

export const sanitizeStoreTitle = (
  categoryCode?: string,
  storeName?: string,
  templateId?: string | null,
) => {
  let title = "";
  if (templateId && storeName) {
    title = `${storeName}_${templateId}`;
  } else if (categoryCode && storeName) {
    title = `${categoryCode}_${storeName}`;
  } else {
    return "";
  }
  return title.replace(/\s+/g, "_").replace(/__+/g, "_");
};

export const createBaseProductStub = (code: string, name: string): BaseProduct => ({
  uniqueId: code,
  id: code,
  productCode: code,
  name: name || code,
  upc: "",
  brand: "",
  variant: "",
  categoryCode: "",
  subCategory: "",
  size: "",
  color: "",
  profit: null,
  quantity: 0,
  price: 0,
  salesAmount: 0,
  unitWidth: 0,
  unitHeight: 0,
  unitDepth: 0,
  squeezeWidth: false,
  squeezeWidthPct: 100,
  squeezeHeight: false,
  squeezeHeightPct: 100,
  squeezeDepth: false,
  squeezeDepthPct: 100,
  cdt1: "",
  cdt1Priority: 0,
  cdt2: "",
  cdt2Priority: 0,
  cdt3: "",
  cdt3Priority: 0,
  facings: 0,
  minFacings: 0,
  maxFacings: 0,
  minOnShelf: 0,
  minDaysOfSupply: 0,
  maxDaysOfSupply: 0,
  inCoreRange: false,
  newItemFlag: false,
  dndFlag: false,
  spaceMix: 0,
  ranking: 0,
});
